import React, { useState, Suspense } from "react";
import { Route, BrowserRouter as Router, Routes, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import SessionManager from "components/SessionManager";
import DisclaimerBanner from "components/DisclaimerBanner";
import NotFound from "pages/NotFound";
import CookieConsentBanner from "components/CookieConsentBanner";
import Sidebar from './components/ui/Sidebar';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthContext';

// Lazy-loaded page components
const PatientManagement = React.lazy(() => import('./pages/patient-management'));
const AppointmentManagement = React.lazy(() => import('./pages/appointment-management'));
const Login = React.lazy(() => import('./pages/login'));
const Communications = React.lazy(() => import('./pages/communications'));
const ClaimsHelper = React.lazy(() => import('./pages/claims-helper'));
const Dashboard = React.lazy(() => import('./pages/dashboard'));
const PatientDetail = React.lazy(() => import('./pages/patient-detail'));
const IntegrationManagement = React.lazy(() => import('./pages/integration-management'));
const AppointmentDetail = React.lazy(() => import('./pages/appointment-detail'));
const PracticeSetup = React.lazy(() => import('./pages/practice-setup'));
const ClinicalNotes = React.lazy(() => import('./pages/clinical-notes'));
const RevenueAnalytics = React.lazy(() => import('./pages/revenue-analytics'));
const PerioChart = React.lazy(() => import('./pages/perio-chart'));
const InsuranceManagement = React.lazy(() => import('./pages/insurance-management'));
const InsuranceEligibility = React.lazy(() => import('./pages/insurance-eligibility'));
const ClaimsProcessing = React.lazy(() => import('./pages/claims-processing'));
const OraFusionAIHome = React.lazy(() => import('./pages/ora-fusion-ai-home'));
const MarketingPresentation = React.lazy(() => import('./pages/marketing-presentation'));
const TechnicalDocumentationCenter = React.lazy(() => import('./pages/technical-documentation-center'));
const ScheduleDemo = React.lazy(() => import('./pages/schedule-demo'));
const TrialSignup = React.lazy(() => import('./pages/trial-signup'));
const PatientFeedback = React.lazy(() => import('./pages/patient-feedback'));
const PatientPortal = React.lazy(() => import('./pages/patient-portal'));
const AIRevenueForecasting = React.lazy(() => import('./pages/ai-revenue-forecasting'));
const AIClaimsAnalysis = React.lazy(() => import('./pages/ai-claims-analysis'));
const DeniedClaimsManagement = React.lazy(() => import('./pages/denied-claims-management'));
const EligibilityReVerificationScheduler = React.lazy(() => import('./pages/eligibility-reverification-scheduler'));
const HipaaAuditCenter = React.lazy(() => import('./pages/hipaa-audit-center'));
const SystemHealthMonitor = React.lazy(() => import('./pages/system-health-monitor'));
const LiveActivityDashboard = React.lazy(() => import('./pages/live-activity-dashboard'));
const PracticeBenchmarkingDashboard = React.lazy(() => import('./pages/practice-benchmarking-dashboard'));
const PatientOutcomes = React.lazy(() => import('./pages/patient-outcomes'));
const AIAgentOrchestrator = React.lazy(() => import('./pages/ai-agent-orchestrator'));
const AIActionPanel = React.lazy(() => import('./pages/ai-action-panel'));
const AgenticWorkflowAutomation = React.lazy(() => import('./pages/agentic-workflow-automation'));
const RAGAdminIngestion = React.lazy(() => import('./pages/rag-admin-ingestion'));
const RAGAnalyticsDashboard = React.lazy(() => import('./pages/rag-analytics-dashboard'));
const AICostDashboard = React.lazy(() => import('./pages/ai-cost-dashboard'));
const PromptRetrievalLab = React.lazy(() => import('./pages/prompt-retrieval-lab'));
const DeploymentCenter = React.lazy(() => import('./pages/deployment-center'));
const AllPages = React.lazy(() => import('./pages/all-pages'));
const MonitoringDashboard = React.lazy(() => import('./pages/monitoring-dashboard'));
const LoadTestDashboard = React.lazy(() => import('./pages/load-test-dashboard'));
const ObservabilityIntegrations = React.lazy(() => import('./pages/observability-integrations'));
const UnifiedObservabilityDashboard = React.lazy(() => import('./pages/unified-observability-dashboard'));
const TwilioSmsNotifications = React.lazy(() => import('./pages/twilio-sms-notifications'));
const SystemErrorRecovery = React.lazy(() => import('./pages/system-error-recovery'));
const OnboardingTraining = React.lazy(() => import('./pages/onboarding-training'));
const PracticeRoleManagement = React.lazy(() => import('./pages/practice-role-management'));
const RadiologyGalleryCenter = React.lazy(() => import('./pages/radiology-gallery-center'));
const AgenticGuardrailsDashboard = React.lazy(() => import('./pages/agentic-guardrails'));
const DataMigrationTool = React.lazy(() => import('./pages/data-migration-tool'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const DashboardLayout = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="flex">
      <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
      <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// Component to initialize Google Analytics tracking inside Router context
const GoogleAnalyticsTracker = () => {
  useGoogleAnalytics();
  return null;
};

const SuspenseFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0d0f14]">
    <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function ProjectRoutes() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
          <GoogleAnalyticsTracker />
          <ScrollToTop />
          <SessionManager />
          <DisclaimerBanner />
          <CookieConsentBanner />
          <Suspense fallback={<SuspenseFallback />}>
            <Routes>
              {/* Home Route - New Landing Page */}
              <Route path="/" element={<OraFusionAIHome />} />
              <Route path="/ora-fusion-ai-home" element={<OraFusionAIHome />} />
              <Route path="/schedule-demo" element={<ScheduleDemo />} />
              <Route path="/trial-signup" element={<TrialSignup />} />
              
              {/* Login Route - No Layout */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected Routes with DashboardLayout */}
              <Route path="/patient-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PatientManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/appointment-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AppointmentManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/communications" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Communications />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/claims-helper" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ClaimsHelper />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/patient-detail" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PatientDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/integration-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <IntegrationManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/appointment-detail" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AppointmentDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/practice-setup" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PracticeSetup />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/clinical-notes" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ClinicalNotes />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/revenue-analytics" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RevenueAnalytics />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/marketing-presentation" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MarketingPresentation />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/perio-chart" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PerioChart />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/insurance-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InsuranceManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/insurance-eligibility" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InsuranceEligibility />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/claims-processing" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ClaimsProcessing />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/technical-documentation-center" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TechnicalDocumentationCenter />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/patient-feedback" element={<ProtectedRoute><DashboardLayout><PatientFeedback /></DashboardLayout></ProtectedRoute>} />
              <Route path="/patient-portal" element={<ProtectedRoute><DashboardLayout><PatientPortal /></DashboardLayout></ProtectedRoute>} />
              <Route path="/ai-revenue-forecasting" element={<ProtectedRoute><DashboardLayout><AIRevenueForecasting /></DashboardLayout></ProtectedRoute>} />
              <Route path="/ai-claims-analysis" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AIClaimsAnalysis />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/denied-claims-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DeniedClaimsManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/eligibility-reverification-scheduler" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <EligibilityReVerificationScheduler />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/hipaa-audit-center" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <HipaaAuditCenter />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/system-health-monitor" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SystemHealthMonitor />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/live-activity-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LiveActivityDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/practice-benchmarking-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PracticeBenchmarkingDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/patient-outcomes" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PatientOutcomes />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai-agent-orchestrator" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AIAgentOrchestrator />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai-action-panel" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AIActionPanel />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/agentic-workflow-automation" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AgenticWorkflowAutomation />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/rag-admin-ingestion" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RAGAdminIngestion />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/rag-analytics-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RAGAnalyticsDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/ai-cost-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AICostDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/prompt-retrieval-lab" element={<ProtectedRoute><DashboardLayout><PromptRetrievalLab /></DashboardLayout></ProtectedRoute>} />
              <Route path="/deployment-center" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DeploymentCenter />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/monitoring-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <MonitoringDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/load-test-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LoadTestDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/observability-integrations" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ObservabilityIntegrations />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/unified-observability-dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <UnifiedObservabilityDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/twilio-sms-notifications" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TwilioSmsNotifications />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/system-error-recovery" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SystemErrorRecovery />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/onboarding-training" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <OnboardingTraining />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/practice-role-management" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PracticeRoleManagement />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/radiology-gallery-center" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RadiologyGalleryCenter />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/agentic-guardrails" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AgenticGuardrailsDashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/data-migration-tool" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DataMigrationTool />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/all-pages" element={<ProtectedRoute><DashboardLayout><AllPages /></DashboardLayout></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default ProjectRoutes;